import {
    BigId,
    SESSION_ESTABLISHED_MESSAGE, SOCKET_CLOSE_KICKED, SOCKET_CLOSE_SERVER_TOO_SLOW, SOCKET_CLOSE_USER_CHANGED,
    SocketData,
} from '@colonist/types'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables'
import {logDebugInfo, logError} from '../ui_utils/Utils'
import * as MsgPack from '@msgpack/msgpack'
import {NetworkEnums} from '../../../../shared/GameEnums'
import {StorageWrapper} from '../StorageWrapper'
import {getHomepageUrl, navigateToPage} from '../ui_utils/ClientUtils'
import {CA} from '../analytics/Analytics'
import {ClientUserState} from '../ClientUserState'
import {createDeferredPromise, TimeUtils} from '@colonist/utils'

export class SocketClient {
    private readonly serverUrl: string
    private webSocket: WebSocket | undefined
    private currentUserId: BigId | undefined

    private readonly onSessionEstablished: () => void
    private readonly onStateUpdate: (state: NetworkEnums.State, data: any) => void
    private readonly onClose: () => void

    constructor(serverUrl: string, onSessionEstablished: () => void, onStateUpdate: (state: NetworkEnums.State, data: any) => void, onClose: () => void) {
        this.serverUrl = serverUrl
        this.onSessionEstablished = onSessionEstablished
        this.onStateUpdate = onStateUpdate
        this.onClose = onClose

        ClientUserState.registerChangeListener(() => {
            this.checkUserChanged()
        })
    }

    isSocketOpen(): boolean {
        return this.webSocket?.readyState === WebSocket.OPEN
    }

    async connect(): Promise<boolean> {
        if(ClientUserState.userState == undefined) return false

        const resultPromise = createDeferredPromise<boolean>()

        const connectTimeout = 9 * TimeUtils.MILLISECONDS
        setTimeout(() => {
            if(!resultPromise.pending) return

            this.close(SOCKET_CLOSE_SERVER_TOO_SLOW)
            resultPromise.resolve(false)
        }, connectTimeout)

        this.webSocket = new WebSocket(this.getWebSocketUrl())
        this.webSocket.binaryType = 'arraybuffer'

        this.currentUserId = ClientUserState.userState?.id

        this.webSocket.onopen = () => this.checkUserChanged()

        this.webSocket.onmessage = (message) => {
            if(this.webSocket == undefined) return // TS only
            if(message.data == SESSION_ESTABLISHED_MESSAGE) {
                this.webSocket.onmessage = (normalMessage) => this.handleWebSocketMessage(normalMessage)
                this.onSessionEstablished()
                resultPromise.resolve(true)
                return
            }

            this.handleWebSocketMessage(message)
        }

        this.webSocket.onclose = (ev) => {
            this.handleWebSocketClose(ev.code)
            resultPromise.resolve(false)
        }

        this.webSocket.onerror = (error) => {
            this.handleWebSocketError(error)
            resultPromise.resolve(false)
        }

        return resultPromise
    }

    private getWebSocketUrl(): string {
        let token = ''
        if(UIUtilsEjsVariables.isUserOnDiscordMobile) {
            const authToken = StorageWrapper.getAuthToken()
            if(authToken != null) token = `?${authToken}`
        }

        return `${this.serverUrl}${token}`
    }

    private handleWebSocketMessage(message: MessageEvent) {
        const dataObject = MsgPack.decode(message.data) as SocketData
        if(UIUtilsEjsVariables.socketDebugActive) {
            logDebugInfo('SocketMessage', [
                '======',
                `Message type: ${dataObject.id}, data:`,
                dataObject.data,
            ])
        }

        this.onStateUpdate(Number(dataObject.id) as NetworkEnums.State, dataObject.data)
    }

    private handleWebSocketError(error: Event) {
        if(UIUtilsEjsVariables.socketDebugActive) logError('connect', ['Closing socket, encountered error: ', error])
        this.webSocket?.close()
    }

    private handleWebSocketClose(code: number) {
        CA.sendGoogleAnalyticsEvent(CA.Category.General, CA.Label.GeneralBrokenInternetConnection)
        this.currentUserId = undefined

        if(code == SOCKET_CLOSE_KICKED) {
            navigateToPage(getHomepageUrl())
            return
        }

        this.onClose()
    }

    close(code?: number) {
        this.webSocket?.close(code)
    }

    private checkUserChanged() {
        if(!this.isSocketOpen()) return
        if(ClientUserState.userState?.id != this.currentUserId) {
            this.close(SOCKET_CLOSE_USER_CHANGED) // auto reconnect will connect with new user
        }
    }

    send(binarySocketMessage: Uint8Array) {
        if(!this.isSocketOpen()) return
        this.webSocket?.send(binarySocketMessage)
    }
}
